import React, { useState, useEffect } from "react";
import { Link } from "react-router";
import moment from "moment";
import axios from "axios";
import ReactTooltip from "react-tooltip";
import { smoothScrollToTop } from "../../functions.js";

import { CourseStateTooltip } from "../courses/components/tooltips";
import { courseStateNames } from "../courses/components/courseState";
import { responseToDownload, downloadFile } from "../../functions.js";
import { addGlobalMessage } from "../../actions/globalmessages";
import { useDispatch } from "react-redux";

export default function CoursePassDetail({ params, router, ...props }) {
    const [course, setCourse] = useState(null);
    const [courseStudents, setCourseStudents] = useState([]);
    const [selectedCertifications, setSelectedCertifications] = useState([]);
    const [exportPassFile, setExportPassFile] = useState([]);
    const [exportLoading, setExportLoading] = useState(false);
    const [exportPassesLoading, setExportPassesLoading] = useState(false);
    const dispatch = useDispatch();

    const getCourseData = async () => {
        const response = await axios.get(`/v1/certification-passes/${params.uuid}/`);
        setCourse(response.data);
    };

    const getStudentData = async () => {
        setExportPassesLoading(true);
        const response = await axios.get(
            `/v1/certification-passes/${params.uuid}/certifications/?fields=uuid,date,user,competence_profile,created,printed,printed_by,end_terms&pagination=false`
        );
        setCourseStudents(response.data);
        setExportPassesLoading(false);
    };

    const getExportPassData = async () => {
        const response = await axios.get(`/v1/pass-export-files/?course=${params.uuid}&user=${props.authentication?.user.uuid}`);
        setExportPassFile(response.data.results);
    };
    const export_url = exportPassFile[0]?.uuid ? `v1/pass-export-files/${exportPassFile[0]?.uuid}/` : "";

    function handleDownloadZIPPasses(event, url, fileName = "") {
        event.stopPropagation();
        event.preventDefault();
        downloadFile(url, fileName);
    }

    const formatLatestRequestDate = () => {
        if (!Array.isArray(courseStudents) || courseStudents.length === 0) {
            return "N.N.B.";
        }

        const today = new Date();
        let closestStudent = courseStudents[0];
        let closestDate = new Date(closestStudent.physical_pass_date || closestStudent.created);
        let smallestDiff = Math.abs(closestDate - today);

        courseStudents.forEach((student) => {
            const studentDate = new Date(student.physical_pass_date || student.created);
            const diff = Math.abs(studentDate - today);

            if (diff < smallestDiff) {
                smallestDiff = diff;
                closestStudent = student;
                closestDate = studentDate;
            }
        });

        return moment(closestDate).format("DD-MM-YYYY");
    };

    useEffect(() => {
        getCourseData();
        getStudentData();
        getExportPassData();
    }, []);

    const toggleCertificationSelect = (certificationID, pass) => {
        if (selectedCertifications?.includes(certificationID)) {
            setSelectedCertifications(selectedCertifications?.filter((uuid) => uuid !== certificationID));
        } else {
            setSelectedCertifications([...selectedCertifications, certificationID]);
        }
    };

    const toggleSelectAll = () => {
        if (allCertificationsSelected) {
            setSelectedCertifications([]);
        } else {
            setSelectedCertifications(courseStudents.map((certification) => certification.uuid));
        }
    };

    const allCertificationsSelected = selectedCertifications?.length === courseStudents?.length;

    const downloadPassesZIP = async () => {
        setExportLoading(true);
        const payload = selectedCertifications?.map((certificationID) => ({ certification: certificationID }));

        return new Promise((resolve, reject) => {
            axios({
                url: `/v1/certification-passes/${params.uuid}/download/`,
                data: payload,
                method: "POST",
                responseType: "blob",
                headers: {
                    Accept: "*/*"
                }
            })
                .then((response) => {
                    responseToDownload(response, "certification-pass-" + course.id + ".zip");
                    resolve(response);
                    // fetch some data again:
                    getCourseData();
                    getStudentData();
                    setExportLoading(false);
                })
                .catch((error) => {
                    console.log(error);
                    reject(error);
                });
        });
    };

    const exportPasses = async () => {
        setExportLoading(true);
        const payload = selectedCertifications?.map((certificationID) => ({ certification: certificationID }));

        return new Promise((resolve, reject) => {
            // Check if there are more than 100 selected certifications
            if (payload.length > 100) {
                setExportLoading(false);
                dispatch(
                    addGlobalMessage({
                        title: "Er zijn meer dan 100 passen geselecteerd voor export. Neem contact op met de ontwikkelaars voor deze actie.",
                        type: "error",
                        icon: "fa fa-exclamation-triangle"
                    })
                );
                smoothScrollToTop(600);
                return;
            }

            axios({
                url: `/v1/certification-passes/${params.uuid}/export/`,
                data: payload,
                method: "POST",
                responseType: "blob",
                headers: {
                    Accept: "*/*"
                }
            })
                .then((response) => {
                    resolve(response);
                    getCourseData();
                    getStudentData();
                    setExportLoading(false);
                    dispatch(
                        addGlobalMessage({
                            title: "Uw rapport staat in de wachtrij. U ontvangt het per mail wanneer het klaar is."
                        })
                    );
                    smoothScrollToTop(600);
                })
                .catch((error) => {
                    console.log(error);
                    reject(error);
                });
        });
    };

    return (
        <>
            <div className="section-top">
                <div className="breadcrumbs">
                    <Link to="/passes">Passen</Link>
                </div>
                <h1>
                    <strong>Passen administratie voor cursus {course?.id}</strong>
                </h1>
            </div>
            <div className="row">
                <div className="col-12 filters mb-2">
                    <h2>Cursus informatie</h2>
                </div>
                <div className="col-12">
                    {course ? (
                        <>
                            <div className="row mb-2">
                                <div className="col-4">ID</div>
                                <div className="col-8">
                                    <strong
                                        className="link"
                                        onClick={() => {
                                            window.open("/courses/edit/" + course.uuid + "/students");
                                        }}
                                    >
                                        {course.id}
                                    </strong>
                                </div>
                            </div>
                            <div className="row mb-2">
                                <div className="col-4">Referentie</div>
                                <div className="col-8">
                                    <strong>{course.your_reference}</strong>
                                </div>
                            </div>
                            <div className="row mb-2">
                                <div className="col-4">Cursus</div>
                                <div className="col-8">
                                    <strong>{course.display_name}</strong>
                                </div>
                            </div>
                            <div className="row mb-2">
                                <div className="col-4">Einddatum</div>
                                <div className="col-8">
                                    <strong>{moment(course.end_datetime).format("DD-MM-YYYY HH:mm")}</strong>
                                </div>
                            </div>

                            <div className="row mb-2">
                                <div className="col-4">Opleider</div>
                                <div className="col-8">
                                    <strong>{course.trainer.name}</strong>
                                </div>
                            </div>
                            <div className="row mb-2">
                                <div className="col-4">Keurmerknr</div>
                                <div className="col-8">
                                    <span>{course.trainer.qualitymark_number}</span>
                                    {course.trainer.qualitymark_number ? (
                                        <i className="fa fa-check" style={{ color: "green" }} />
                                    ) : (
                                        <i className="fa fa-times" style={{ color: "red" }} />
                                    )}
                                </div>
                            </div>

                            <div className="row mb-2">
                                <div className="col-4">Aanvraagdatum</div>
                                <div className="col-8">
                                    <strong>{formatLatestRequestDate()}</strong>
                                </div>
                            </div>
                            <div className="row mb-2">
                                <div className="col-4">
                                    Status
                                    <CourseStateTooltip />
                                </div>
                                <div className="col-8">
                                    <span className={"course-status-indicator course-status-indicator--" + course?.state}>
                                        {courseStateNames?.find((item) => item.slug === course?.state)?.name}
                                    </span>
                                </div>
                            </div>
                            <div className="row mb-2"></div>
                            {exportPassesLoading && <div style={{ float: "right" }} className="print-students-loader"></div>}

                            <table className="table">
                                <thead>
                                    <tr>
                                        <th className="overview-redux__head overview-redux__head--bulk-checkbox">
                                            <input
                                                type="checkbox"
                                                title="Selecteer alle items"
                                                className="checkbox checkbox--no-label"
                                                onChange={toggleSelectAll}
                                                checked={allCertificationsSelected}
                                            />
                                        </th>
                                        <th width="140">NIBHV #</th>
                                        <th>Cursist</th>
                                        <th>Geboortedatum</th>
                                        <th width="210" className="pass-achieved-endterms-header">
                                            <span>Behaalde eindtermen</span>
                                        </th>
                                        <th>Aanvraagdatum</th>
                                        <th>Geprint door</th>
                                        <th>Printdatum</th>
                                    </tr>
                                </thead>
                                <tbody className="pass_table">
                                    {courseStudents?.map((certification) => {
                                        return (
                                            <tr key={certification.uuid}>
                                                <td>
                                                    <input
                                                        onChange={() => toggleCertificationSelect(certification.uuid)}
                                                        checked={selectedCertifications?.includes(certification.uuid)}
                                                        type="checkbox"
                                                        // disabled={!certification.physical_pass}
                                                        className="checkbox checkbox--no-label"
                                                    />
                                                </td>
                                                <td width="140">{certification.user.nibhv_number}</td>
                                                <td>{certification.user.formal_name}</td>
                                                <td>{certification.user.date_of_birth}</td>
                                                <td width="210" className="cellContent" data-tip data-for={`certification-${certification.uuid}`}>
                                                    {certification.end_terms.map((endTerm) => (
                                                        <div key={endTerm.uuid} className="pass-detail-achieved-endterm">
                                                            <i className="fa fa-check" />
                                                            {endTerm.name} op {moment(endTerm.date).format("DD-MM-YYYY")}
                                                        </div>
                                                    ))}
                                                    <ReactTooltip
                                                        id={`certification-${certification.uuid}`}
                                                        place="right"
                                                        effect="solid"
                                                        offset={{ left: 30 }}
                                                        container={document.body}
                                                        getContent={() => (
                                                            <div className="tooltip-content">
                                                                {certification.end_terms.map((endTerm) => (
                                                                    <div key={endTerm.uuid} className="tooltip-endterm">
                                                                        {endTerm.name} op {moment(endTerm.date).format("DD-MM-YYYY")}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    />
                                                </td>
                                                <td>{moment(certification.physical_pass_date || certification.date).format("DD-MM-YYYY")}</td>
                                                <td>{certification.printed_by?.full_name}</td>
                                                <td>{certification.printed && moment(certification.printed).format("DD-MM-YYYY")}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            {export_url && (
                                <a href="#" onClick={(e) => handleDownloadZIPPasses(e, export_url)}>
                                    <i className="fa fa-download" style={{ marginRight: "5px" }} />
                                    Download passen ({moment(exportPassFile[0]?.modified).format("DD-MM-YYYY")})
                                </a>
                            )}

                            <button
                                data-tip
                                data-for="print-students-icon"
                                className="btn btn-primary no-sideflap print-students-button"
                                style={{ float: "right" }}
                                disabled={!selectedCertifications?.length || exportLoading}
                                // ToDo temporaly solution, in order to investigate what happened with download more passes
                                // onClick={selectedCertifications?.length > 10 ? exportPasses : downloadPassesZIP}
                                onClick={selectedCertifications?.length > 5 ? exportPasses : downloadPassesZIP}
                            >
                                Print geselecteerde passen
                                {exportLoading ? <div className="print-students-loader"></div> : <i className="fa fa-list"></i>}
                            </button>
                        </>
                    ) : (
                        <div className="spinner-border spinner-border-sm float-right" role="status">
                            <span className="sr-only">Bezig met laden...</span>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
